import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import swal from 'sweetalert';
import { Pagination } from 'semantic-ui-react';
import '../../../styles/admin-cms.scss';

function statusBadge(status, published) {
	const value = status || (published ? 'published' : 'draft');
	const labels = {
		draft: 'Draft',
		review: 'In review',
		published: 'Published',
		archived: 'Archived',
	};
	const cls = `admin-cms-status admin-cms-status--${value.replace('_', '-')}`;
	return <span className={cls}>{labels[value] || value}</span>;
}

export default function CmsAdminList({
	title,
	description,
	backHref = '/admin/cms',
	addHref,
	formPath,
	resourceKey,
	fetchList,
	deleteItem,
	duplicateItem,
	getRowId = (row) => row.slug,
	getRowTitle = (row) => row.title || row.name,
	getRowSubtitle = (row) => row.region || row.category || row.media_type || '',
	getViewOnSiteHref,
	getAppearsOnSummary,
}) {
	const history = useHistory();
	const location = useLocation();
	const [rows, setRows] = useState([]);
	const [meta, setMeta] = useState({});
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('');

	const fetchRows = useCallback((page = 1) => {
		setLoading(true);
		const params = {
			page,
			admin_list: 'true',
			...(search ? { search } : {}),
		};
		fetchList(params)
			.then((res) => {
				const data = res.data || {};
				const list = data[resourceKey] || data.journeys || data.destinations || data.festivals || data.guides || data.content_media || data.official_sources || data || [];
				setRows(Array.isArray(list) ? list : []);
				setMeta(data.meta || {});
			})
			.catch(() => setRows([]))
			.finally(() => setLoading(false));
	}, [fetchList, resourceKey, search]);

	useEffect(() => {
		const page = new URLSearchParams(location.search).get('page') || 1;
		fetchRows(Number(page));
	}, [fetchRows, location.search]);

	const handleDelete = (row) => {
		const id = getRowId(row);
		swal({
			title: 'Delete this item?',
			text: 'This cannot be undone.',
			icon: 'warning',
			buttons: true,
			dangerMode: true,
		}).then((ok) => {
			if (!ok) return;
			deleteItem(id)
				.then(() => {
					swal('Deleted', '', 'success');
					fetchRows();
				})
				.catch((err) => swal('Error', err.response?.data?.errors?.[0] || 'Could not delete', 'error'));
		});
	};

	const handleDuplicate = (row) => {
		if (!duplicateItem) return;
		duplicateItem(getRowId(row))
			.then(() => {
				swal('Duplicated', 'A draft copy was created.', 'success');
				fetchRows();
			})
			.catch((err) => swal('Error', err.response?.data?.errors?.[0] || 'Could not duplicate', 'error'));
	};

	const pag = meta.pagination || meta || {};
	const totalPages = pag.total_pages || 1;
	const currentPage = pag.current_page || 1;

	return (
		<div className="admin-cms-list">
			<Link to={backHref} className="admin-cms-back">← Content</Link>
			<header className="admin-cms-list__head">
				<div>
					<h1>{title}</h1>
					{description && <p>{description}</p>}
				</div>
				{addHref && <Link to={addHref} className="btn btn-primary">+ Add new</Link>}
			</header>

			<div className="admin-cms-list__filters">
				<input
					type="search"
					placeholder="Search by title…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && fetchRows(1)}
				/>
				<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => fetchRows(1)}>Search</button>
			</div>

			{loading ? (
				<p className="admin-cms-muted">Loading…</p>
			) : (
				<div className="table-responsive">
					<table className="table admin-cms-table">
						<thead>
							<tr>
								<th>Title</th>
								<th>Details</th>
								<th>Status</th>
								<th>Updated</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && (
								<tr><td colSpan={5} className="admin-cms-muted">No items yet. Create the first one.</td></tr>
							)}
							{rows.map((row) => (
								<tr key={getRowId(row)}>
									<td><strong>{getRowTitle(row)}</strong><br /><small className="text-muted">{getRowId(row)}</small></td>
									<td>
										{getRowSubtitle(row)}
										{getAppearsOnSummary && (
											<div className="admin-cms-table__appears">{getAppearsOnSummary(row)}</div>
										)}
									</td>
									<td>{statusBadge(row.editorial_status, row.published)}</td>
									<td>{row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}</td>
									<td className="admin-cms-table__actions">
										{getViewOnSiteHref && getViewOnSiteHref(row) && (
											<a
												href={getViewOnSiteHref(row)}
												target="_blank"
												rel="noopener noreferrer"
												className="btn btn-sm btn-success"
											>
												View on site
											</a>
										)}
										<Link to={`${formPath}/${getRowId(row)}`} className="btn btn-sm btn-outline-primary">Edit</Link>
										{duplicateItem && (
											<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleDuplicate(row)}>Duplicate</button>
										)}
										<button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row)}>Delete</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{totalPages > 1 && (
				<Pagination
					boundaryRange={1}
					siblingRange={1}
					activePage={currentPage}
					totalPages={totalPages}
					onPageChange={(e, { activePage }) => {
						history.push(`${location.pathname}?page=${activePage}`);
					}}
				/>
			)}
		</div>
	);
}
